import { SafetyLocalStorage } from '@mail-core/std';
import { Button } from '@vkontakte/vkui';
import { getFeature } from 'Cloud/Application/FeaturesEs6';
import freemiumFeaturesImg from 'img/family-content/freemium/freemium-features-tooltip.png';
import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { USER_EMAIL } from 'reactApp/appHelpers/configHelpers';
import { FloatingTooltip } from 'reactApp/ui/FloatingTooltip/FloatingTooltip';
import { ETooltipPlacement, ETooltipSize } from 'reactApp/ui/FloatingTooltip/FloatingTooltip.types';
import { sendDwh } from 'reactApp/utils/ga';

import styles from './FamilyFreemiumFeaturesTooltip.css';

interface FamilyFreemiumFeaturesTooltipProps {
    onClose?: () => void;
    onShow?: () => void;
    onClick?: () => void;
    onCrossClose?: () => void;
    leftOffset?: number;
    placement?: ETooltipPlacement;
    closeOnOutsideClick?: boolean;
    targetSelector?: string;
}

const TOOLTIP_CLOSED_KEY = 'family_freemium_features_tooltip_closed';
const AUTO_CLOSE_DELAY = 20000;

const email = USER_EMAIL;
const lStorage = new SafetyLocalStorage({});

// определяет тип фримиума для аналитики
const getFreemiumTypeForAnalytics = (): 'soft' | 'hard' | 'trial' => {
    const freemiumTypeParam = getFeature('freemium-type') as 'soft' | 'hard' | undefined;
    if (freemiumTypeParam === 'soft') {
        return 'soft';
    }
    if (freemiumTypeParam === 'hard') {
        return 'hard';
    }
    return 'trial';
};

// получает имя для аналитики тултипа
export const getTooltipAnalyticsName = (): string => {
    const type = getFreemiumTypeForAnalytics();
    if (type === 'soft') {
        return 'family_soft_freemium_time_empty';
    }
    if (type === 'hard') {
        return 'family_hard_freemium_time_empty';
    }
    return 'family_trial_time_empty';
};

export const FamilyFreemiumFeaturesTooltip = forwardRef<HTMLDivElement, FamilyFreemiumFeaturesTooltipProps>(
    ({
        onClose,
        onClick,
        onCrossClose,
        leftOffset,
        placement = ETooltipPlacement.bottomEnd,
        closeOnOutsideClick = true,
        targetSelector = '[data-qa-id="family-freemium-features"]',
    }) => {
        const tooltipAnalyticsName = getTooltipAnalyticsName();
        const autoCloseTimerRef = useRef<NodeJS.Timeout | null>(null);
        const [isOpen, setIsOpen] = useState(true);

        const handleClose = useCallback(() => {
            if (autoCloseTimerRef.current) {
                clearTimeout(autoCloseTimerRef.current);
            }
            setIsOpen(false);
        }, []);

        useEffect(() => {
            autoCloseTimerRef.current = setTimeout(() => {
                sendDwh({
                    eventCategory: 'tooltip',
                    action: 'close',
                    dwh_enabled: true,
                    dwhData: {
                        name: tooltipAnalyticsName,
                    },
                });
                handleClose();
            }, AUTO_CLOSE_DELAY);

            return () => {
                if (autoCloseTimerRef.current) {
                    clearTimeout(autoCloseTimerRef.current);
                }
            };
        }, [handleClose, tooltipAnalyticsName]);

        const handleAddClick = useCallback(() => {
            if (autoCloseTimerRef.current) {
                clearTimeout(autoCloseTimerRef.current);
            }
            sendDwh({
                eventCategory: 'tooltip',
                action: 'close',
                dwh_enabled: true,
                dwhData: {
                    name: tooltipAnalyticsName,
                },
            });
            lStorage.setItem(`${email}|${TOOLTIP_CLOSED_KEY}`, 'true');
            onClick?.();
            handleClose();
        }, [onClick, handleClose]);

        const handleCrossClose = useCallback(() => {
            if (autoCloseTimerRef.current) {
                clearTimeout(autoCloseTimerRef.current);
            }
            sendDwh({
                eventCategory: 'tooltip',
                action: 'close',
                dwh_enabled: true,
                dwhData: {
                    name: tooltipAnalyticsName,
                },
            });
            lStorage.setItem(`${email}|${TOOLTIP_CLOSED_KEY}`, 'true');
            onCrossClose?.();
            handleClose();
        }, [onCrossClose, handleClose]);

        const targetRef = document.querySelector(targetSelector) as HTMLDivElement;

        if (!isOpen) {
            return null;
        }

        return (
            <FloatingTooltip
                target={targetRef}
                placement={placement}
                size={ETooltipSize.fit}
                onClose={() => {
                    handleClose();
                    onClose?.();
                }}
                onCrossClose={handleCrossClose}
                usePortal
                qaId="family-freemium-features-tooltip"
                leftOffset={leftOffset}
                closeOnOutsideClick={closeOnOutsideClick}
            >
                <div className={styles.root}>
                    <div className={styles.textBlock}>
                        <div className={styles.title}>Тут можно посмотреть, какие возможности есть у подписки</div>
                        <div className={styles.text}>
                            И протестировать всё, пока
                            <br />
                            действует пробный период
                        </div>
                    </div>
                    <Button onClick={handleAddClick} className={styles.button} size="l" stretched={false}>
                        Хорошо
                    </Button>
                    <img className={styles.image} src={freemiumFeaturesImg} alt="Семья" />
                </div>
            </FloatingTooltip>
        );
    }
);

FamilyFreemiumFeaturesTooltip.displayName = 'FamilyFreemiumFeaturesTooltip';
